import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import SignaturePad from "../components/SignaturePad";
import { CheckCircle, PencilLine, ClipboardText } from "@phosphor-icons/react";

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function SignOff() {
  const { t } = useI18n();
  const [entries, setEntries] = useState([]);
  const [ranchMap, setRanchMap] = useState({});
  const [weekStart, setWeekStart] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const weekEnd = addDays(weekStart, 6);

  const [selected, setSelected] = useState(null); // { employee_id, employee_name, weekEntries }
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [noInjury, setNoInjury] = useState(false);
  const [noHarassment, setNoHarassment] = useState(false);
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    const { data } = await api.get(`/time-entries?start_date=${weekStart}&end_date=${weekEnd}`);
    setEntries(data);
    try {
      const { data: ranches } = await api.get(`/ranches?include_inactive=true`);
      const m = {};
      ranches.forEach((r) => { m[r.id] = r; });
      setRanchMap(m);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  // Group by employee
  const groups = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      if (!map.has(e.employee_id)) {
        map.set(e.employee_id, { employee_id: e.employee_id, employee_name: e.employee_name, items: [] });
      }
      map.get(e.employee_id).items.push(e);
    });
    return [...map.values()].map((g) => {
      const completed = g.items.filter((e) => e.clock_out);
      const pending = completed.filter((e) => !e.signed && !e.locked);
      const totalHours = completed.reduce((s, e) => s + (e.total_hours || 0), 0);
      const allSigned = completed.length > 0 && completed.every((e) => e.signed);
      return { ...g, completed, pending, totalHours: Math.round(totalHours * 100) / 100, allSigned };
    });
  }, [entries]);

  const pendingGroups = groups.filter((g) => g.pending.length > 0);
  const signedGroups = groups.filter((g) => g.allSigned && g.pending.length === 0);

  const startSign = (g) => {
    setSelected(g);
    setName(g.employee_name || "");
    setSig(null); setNoInjury(false); setNoHarassment(false); setNotes("");
    setMsg("");
  };

  const submit = async () => {
    if (!sig || !name || !noInjury || !noHarassment) {
      setMsg("Please complete name, signature, and both acknowledgments.");
      return;
    }
    try {
      const { data } = await api.post(`/signoff/weekly`, {
        employee_id: selected.employee_id,
        week_start: weekStart,
        signed_by_name: name,
        signature: sig,
        no_injury_confirmed: noInjury,
        no_harassment_confirmed: noHarassment,
        signoff_notes: notes || null,
      });
      setMsg(`Signed ${data.signed} entr${data.signed === 1 ? "y" : "ies"} for the week.`);
      setSelected(null);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="signoff-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("sow.title")}</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">{t("sow.sub")}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.1em] mb-1 block">{t("sow.week_start")}</label>
          <input
            data-testid="signoff-week-input"
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(mondayOf(e.target.value))}
            className="h-12 px-3 border border-stone-200"
          />
          <div className="text-[11px] font-mono text-stone-500 mt-1">{weekStart} → {weekEnd}</div>
        </div>
      </div>

      <section>
        <h2 className="text-xl font-bold uppercase tracking-tight mb-2">{t("so.pending")} ({pendingGroups.length})</h2>
        {pendingGroups.length === 0 ? (
          <div className="bg-white border border-stone-200 p-6 text-center font-bold uppercase text-stone-500 text-sm">{t("so.no_pending")}</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {pendingGroups.map((g) => (
              <button
                key={g.employee_id}
                data-testid={`signoff-pending-${g.employee_id}`}
                onClick={() => startSign(g)}
                className="bg-white border border-stone-200 p-4 shadow-sm shadow-stone-900/5 text-left hover:bg-[#D8C7A1]"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-black uppercase text-lg">{g.employee_name}</div>
                    <div className="text-xs font-mono">{g.pending.length} {t("sow.day_pending")} · {g.totalHours}h {t("sow.total_week")}</div>
                  </div>
                  <PencilLine weight="bold" size={28} />
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {signedGroups.length > 0 && (
        <section>
          <h2 className="text-xl font-bold uppercase tracking-tight mb-2">{t("so.signed")} ({signedGroups.length})</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {signedGroups.map((g) => (
              <div key={g.employee_id} className="bg-[#7C9673]/10 border border-stone-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-black uppercase">{g.employee_name}</div>
                    <div className="text-xs font-mono">{g.completed.length} day(s) · {g.totalHours}h signed</div>
                  </div>
                  <CheckCircle weight="fill" size={28} color="#16A34A" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="signoff-modal">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/10 w-full max-w-xl p-6 max-h-[95vh] overflow-y-auto">
            <h3 className="text-2xl font-black uppercase mb-1" style={{ fontFamily: "'Fraunces', serif" }}>
              <ClipboardText weight="bold" className="inline mr-2" /> {selected.employee_name}
            </h3>
            <p className="text-xs font-mono uppercase mb-4">{t("sow.review_week")}</p>

            <div className="bg-stone-50 border border-stone-200 p-3 mb-4 text-sm">
              <div className="font-medium tracking-wide uppercase text-xs mb-2">Week {weekStart} → {weekEnd}</div>
              <div className="overflow-x-auto max-h-60">
                <table className="w-full text-xs font-mono" data-testid="signoff-day-table">
                  <thead>
                    <tr className="text-left border-b-2 border-stone-300 uppercase tracking-wide text-[10px]">
                      <th className="py-1 pr-2">Date</th>
                      <th className="py-1 pr-2">Ranch</th>
                      <th className="py-1 pr-2">Location</th>
                      <th className="py-1 pr-2 text-right">Total</th>
                      <th className="py-1 pr-2 text-right">OT</th>
                      <th className="py-1 pr-2 text-right">2x OT</th>
                      <th className="py-1 text-right" />
                    </tr>
                  </thead>
                  <tbody>
                    {selected.completed.map((e) => {
                      const loc = e.ranch_location || ranchMap[e.ranch_id]?.location || "";
                      return (
                        <tr key={e.id} className="border-b border-stone-200" data-testid={`signoff-day-row-${e.id}`}>
                          <td className="py-1 pr-2">{e.work_date}</td>
                          <td className="py-1 pr-2">{e.ranch_name || "—"}</td>
                          <td className="py-1 pr-2 text-stone-500">{loc || "—"}</td>
                          <td className="py-1 pr-2 text-right font-bold">{e.total_hours || 0}</td>
                          <td className="py-1 pr-2 text-right">{e.overtime_hours || 0}</td>
                          <td className="py-1 pr-2 text-right">{e.doubletime_hours || 0}</td>
                          <td className="py-1 text-right">
                            {e.signed ? <CheckCircle weight="fill" size={13} color="#16A34A" className="inline" /> : <span className="text-stone-400">·</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-stone-300 font-bold uppercase text-[11px]">
                      <td className="py-1 pr-2" colSpan={3}>{t("sow.total")}</td>
                      <td className="py-1 pr-2 text-right">{selected.totalHours}</td>
                      <td className="py-1 pr-2 text-right">{Math.round(selected.completed.reduce((s, e) => s + (e.overtime_hours || 0), 0) * 100) / 100}</td>
                      <td className="py-1 pr-2 text-right">{Math.round(selected.completed.reduce((s, e) => s + (e.doubletime_hours || 0), 0) * 100) / 100}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">{t("so.your_name")}</label>
            <input
              data-testid="signoff-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-12 px-3 border border-stone-200 mb-4"
            />

            <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">{t("so.signature")}</label>
            <SignaturePad value={sig} onChange={setSig} />

            <label className="flex items-start gap-2 mt-4 cursor-pointer">
              <input data-testid="no-injury-checkbox" type="checkbox" checked={noInjury} onChange={(e) => setNoInjury(e.target.checked)} className="w-5 h-5 mt-0.5" />
              <span className="text-sm font-medium">{t("so.no_injury")}</span>
            </label>
            <label className="flex items-start gap-2 mt-2 cursor-pointer">
              <input data-testid="no-harassment-checkbox" type="checkbox" checked={noHarassment} onChange={(e) => setNoHarassment(e.target.checked)} className="w-5 h-5 mt-0.5" />
              <span className="text-sm font-medium">{t("so.no_harassment")}</span>
            </label>

            <label className="text-sm font-bold uppercase tracking-[0.1em] block mt-4 mb-1">{t("so.notes")}</label>
            <textarea
              data-testid="signoff-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full p-3 border border-stone-200"
            />

            {msg && <div className="mt-3 border border-stone-200 bg-[#D8C7A1] p-2 text-sm font-bold">{msg}</div>}

            <div className="flex gap-2 mt-4">
              <button onClick={() => setSelected(null)} className="flex-1 h-12 bg-white font-bold uppercase border border-stone-200">{t("common.cancel")}</button>
              <button
                data-testid="signoff-submit-btn"
                onClick={submit}
                className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5"
              >
                {t("sow.sign_week")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
